import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number | undefined} */
  #busyTimeout;

  /** Safety net so the loading overlay can't get stuck if the add request fails at the network level. */
  #BUSY_TIMEOUT_MS = 8000;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartError, this.#clearBusy);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartError, this.#clearBusy);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
    // Content has been morphed in by the cart-items component on the same
    // event, so the optimistic loading state can be cleared.
    this.#clearBusy();
  };

  /**
   * Toggles the drawer's loading state. Used to show a brief loading overlay
   * when the drawer is opened optimistically before the add request resolves.
   * @param {boolean} busy - Whether the drawer is awaiting cart content.
   */
  setBusy(busy) {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    if (this.#busyTimeout) {
      clearTimeout(this.#busyTimeout);
      this.#busyTimeout = undefined;
    }

    if (busy) {
      dialog.setAttribute('aria-busy', 'true');
      this.#busyTimeout = setTimeout(this.#clearBusy, this.#BUSY_TIMEOUT_MS);
    } else {
      dialog.removeAttribute('aria-busy');
    }
  }

  #clearBusy = () => {
    this.setBusy(false);
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
